import {useAppContext} from "./useAppContext";
import {contentService} from "../api/contentService";
import {RecommendationType} from "../definitions/enums";
import {ImageType, Nullable} from "../definitions/types";
import {CrossSellModel} from "../models/domain/crossSellModel";
import {useSuspenseQuery} from "@apollo/client";

export const useRecentlyViewedProductQuery = (productId: number) => {
    const {currencyCode} = useAppContext();

    const {
        error,
        data
    } = useSuspenseQuery(contentService.product.recentlyViewed, {
        errorPolicy: 'all',
        variables: {
            id: productId,
            currencyCode: currencyCode,
            type: RecommendationType.UsersWhoViewedAlsoViewed
        }
    });

    const {
        id,
        primaryId,
        designerId,
        sku,
        title,
        collectionName,
        variantPath,
        isDynamicDimension,
        price,
        ratings,
        primaryRatings,
        stockInformation,
        media: {
            images: {
                dot4b
            }
        }
    } = data?.product ?? {
        sku: '',
        ratings: null,
        primaryRatings: null,
        stockInformation: null,
        media: {
            images: {
                dot4b: [] as Nullable<ImageType>[]
            }
        }
    };

    // If there is an error, don't render anything
    if (error) return null;

    // If any of the required properties are missing, don't render anything
    if (!id || !title || !variantPath || !price) return null;

    // Get the first image from the media object (if it exists)
    const images = dot4b.filter((image): image is ImageType => image !== null);

    // If there are no images, don't render anything
    if (images.length === 0) return null;

    return new CrossSellModel({
        id,
        primaryId,
        designerId,
        sku,
        title,
        variantPath,
        collectionName,
        isDynamicDimension,
        customizeIt: !!(isDynamicDimension || designerId),
        minQuantity: stockInformation?.minQuantity ?? 1
    }, {
        ...images[0]
    }, {
        ...price
    }, primaryRatings?.stats ?? ratings?.stats ?? null);
}